// src/layouts/SuperAdminLayout.tsx
// Root layout shell: collapsible sidebar + fixed topbar + scrollable content.
import { useState, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Topbar from './Topbar'

/** Map route → readable title + subtitle. */
const PAGE_META: Record<string, { title: string; subtitle: string }> = {
  '/dashboard': { title: 'Dashboard', subtitle: "Welcome back — here's your platform today" },
  '/reports': { title: 'Reports & Analytics', subtitle: 'Deep-dive into platform metrics' },
  '/institutions': { title: 'Institutions', subtitle: 'Manage affiliated institutions' },
  '/students': { title: 'Students', subtitle: 'All registered learners' },
  '/courses': { title: 'Courses & Syllabus', subtitle: 'Curriculum management' },
  '/exams': { title: 'Exams & Results', subtitle: 'Assessment overview' },
  '/certificates': { title: 'Certificates', subtitle: 'Issued & pending certificates' },
  '/payments': { title: 'Payments', subtitle: 'Transaction history' },
  '/commissions': { title: 'Commissions', subtitle: 'Partner commission tracking' },
  '/staff': { title: 'Staff Management', subtitle: 'Manage staff accounts and their permissions' },
  '/notifications': { title: 'Notification Center', subtitle: 'Alerts and announcements' },
  '/app-updates': { title: 'App Updates', subtitle: 'Mobile version control, instant rollbacks & grace period enforcement' },
  '/configuration': { title: 'Configuration', subtitle: 'System settings' },
  '/profile': { title: 'My Profile', subtitle: 'Manage your profile and account details' },
}

export default function SuperAdminLayout() {
  const [collapsed, setCollapsed] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  })
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('theme')
    if (saved === 'light' || saved === 'dark') {
      return saved
    }
    const systemPrefersDark = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    return systemPrefersDark ? 'dark' : 'light'
  })
  const location = useLocation()

  const meta = PAGE_META[location.pathname] ?? { title: 'Dashboard', subtitle: '' }

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    localStorage.setItem('theme', theme)
  }, [theme])

  const toggleTheme = (e?: React.MouseEvent) => {
    const isSupported = typeof document !== 'undefined' && 'startViewTransition' in document

    if (!isSupported || !e) {
      setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
      return
    }

    const x = e.clientX
    const y = e.clientY
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    )

    const transition = (document as any).startViewTransition(() => {
      flushSync(() => {
        setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
      })
    })

    transition.ready.then(() => {
      const clipPath = [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${endRadius}px at ${x}px ${y}px)`,
      ]
      document.documentElement.animate(
        {
          clipPath: clipPath,
        },
        {
          duration: 500,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      )
    })
  }

  return (
    <div className={`dualon dash-root theme-${theme}`}>
      <div className="dash-cosmos" style={{ '--aurora': 0.9 } as React.CSSProperties} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 1 }}>
        {/* Sidebar */}
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />

        {/* Main column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Topbar
            title={meta.title}
            subtitle={meta.subtitle}
            theme={theme}
            onToggleTheme={toggleTheme}
          />

          {/* Scrollable page content */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-5" style={{ position: 'relative', zIndex: 2 }}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}
